import { type ReactNode, useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { type Board, readRecentBoardIds, useBoards, useMoveBoard } from '@/entities/board';
import {
  activeFilterCount,
  type CatalogFilters,
  effectiveSort,
  type Filterable,
  matchesFilters,
  type ResourceKind,
} from '@/entities/catalog';
import {
  type Database,
  dbCan,
  readRecentDatabaseIds,
  useMoveDatabase,
  useWorkspaceDatabases,
} from '@/entities/database';
import {
  buildTree,
  pathLabel,
  pathOf,
  type Section,
  type SectionNode,
  useMoveSection,
  usePlaceResource,
  useSections,
} from '@/entities/section';
import { useErrorMessage } from '@/shared/i18n';
import { BoardTile } from '@/features/board-manage';
import { DatabaseCard } from '@/features/database-catalog';
import {
  DeleteSectionDialog,
  type MoveSubject,
  MoveToSectionDialog,
  SectionNameDialog,
} from '@/features/workspace-sections';
import { EmptyState, ErrorState, Icons, LoadingState, useToast } from '@/shared/ui';

import styles from './WorkspacePage.module.css';
import { SectionTree } from './SectionTree';
import { WORKSPACE_OFFERED, WORKSPACE_SORTS } from './workspace-filters';

interface Entry {
  key: string;
  kind: ResourceKind;
  name: string;
  /** Position in the manual order: boards first, then databases, each in its own sequence. */
  manual: number;
  rank: number;
  filterable: Filterable;
  node: ReactNode;
}

// --- which sections the viewer collapsed: a personal UI preference of this browser ---------------

const collapsedKey = (workspaceId: string) => `planosfera.sections.collapsed.${workspaceId}`;
const listeners = new Set<() => void>();

function readCollapsed(workspaceId: string): string {
  try {
    return window.localStorage.getItem(collapsedKey(workspaceId)) ?? '[]';
  } catch {
    return '[]';
  }
}

/** Collapsing never touches the server: it is remembered in this browser only. */
function useCollapsed(workspaceId: string) {
  const raw = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => readCollapsed(workspaceId),
    () => '[]',
  );
  const collapsed = useMemo<ReadonlySet<string>>(() => {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return new Set(Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : []);
    } catch {
      return new Set();
    }
  }, [raw]);
  const toggle = useCallback(
    (id: string) => {
      const next = new Set(collapsed);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        window.localStorage.setItem(collapsedKey(workspaceId), JSON.stringify([...next]));
      } catch {
        // Storage unavailable: the state simply is not remembered.
      }
      for (const listener of listeners) listener();
    },
    [collapsed, workspaceId],
  );
  return { collapsed, toggle };
}

function findNode(nodes: readonly SectionNode[], id: string): SectionNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const inside = findNode(node.children, id);
    if (inside) return inside;
  }
  return null;
}

/** "Workspace / Development / Backend": every ancestor navigates, the last one is the page. */
function Breadcrumbs({
  sections,
  focus,
  onFocus,
}: {
  sections: readonly Section[];
  focus: SectionNode;
  onFocus: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const trail = pathOf(sections, focus.id);
  return (
    <nav className={styles.sectionCrumbs} aria-label={t('sections.pathLabel')}>
      <ol>
        <li>
          <button
            type="button"
            onClick={() => {
              onFocus(null);
            }}
          >
            {t('sections.root')}
          </button>
        </li>
        {trail.map((section, index) => (
          <li key={section.id}>
            <span aria-hidden className={styles.crumbSeparator}>
              /
            </span>
            {index === trail.length - 1 ? (
              <span aria-current="page" title={section.name}>
                {section.name}
              </span>
            ) : (
              <button
                type="button"
                title={section.name}
                onClick={() => {
                  onFocus(section.id);
                }}
              >
                {section.name}
              </button>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * The accessible boards and databases of ONE workspace, narrowed by the shared search + filters.
 *
 * Without a search, filter or other sort the catalog is the section HIERARCHY (collapsible
 * branches, a breadcrumb for an open section) and a card can be filed by dragging or from its menu.
 * With any search / filter / sort the list is FLAT across every depth - a resource in a collapsed
 * section is still found, and each result says where it is filed - and nothing can be reordered
 * (a filtered list never defines the canonical order). Objects the viewer cannot reach are absent
 * from the sources (the server never lists them), so neither the tree nor a path can reveal them.
 * Boards and databases keep two independent manual sequences per container.
 */
export function WorkspaceObjects({
  workspaceId,
  kinds,
  query,
  filters,
  locale,
  userId,
  focusId,
  onFocus,
}: {
  workspaceId: string;
  kinds: readonly ResourceKind[];
  query: string;
  filters: CatalogFilters;
  locale: string;
  userId: string | null;
  /** The open section (breadcrumb view); `null` = the workspace root. */
  focusId: string | null;
  onFocus: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const message = useErrorMessage();
  const boards = useBoards(workspaceId);
  const databases = useWorkspaceDatabases(workspaceId);
  const sections = useSections(workspaceId);
  const place = usePlaceResource(workspaceId);
  const moveBoard = useMoveBoard(workspaceId);
  const moveDatabase = useMoveDatabase(workspaceId);
  const moveSection = useMoveSection(workspaceId);
  const { collapsed, toggle } = useCollapsed(workspaceId);
  const [naming, setNaming] = useState<{ parent: Section | null; section?: Section } | null>(null);
  const [deleting, setDeleting] = useState<Section | null>(null);
  const [moving, setMoving] = useState<MoveSubject | null>(null);
  const offered = WORKSPACE_OFFERED;
  const sort = effectiveSort(filters, WORKSPACE_SORTS, 'manual');
  const narrowed =
    sort !== 'manual' || query.trim() !== '' || activeFilterCount(filters, offered) > 0;
  const sectionItems = useMemo(() => sections.data?.items ?? [], [sections.data]);
  const canManage = sections.data?.can_manage ?? false;
  const maxDepth = sections.data?.max_depth ?? 6;
  const roots = useMemo(() => buildTree(sectionItems), [sectionItems]);
  const focus = !narrowed && focusId ? findNode(roots, focusId) : null;
  const filing = sectionItems.length > 0;

  const boardList: readonly Board[] = useMemo(
    () => (kinds.includes('board') ? (boards.data ?? []) : []),
    [boards.data, kinds],
  );
  const databaseList: readonly Database[] = useMemo(
    () => (kinds.includes('database') ? (databases.data ?? []) : []),
    [databases.data, kinds],
  );

  const fail = useCallback(
    (error: unknown) => {
      toast.show({ message: message(error) });
    },
    [toast, message],
  );
  const onDropResource = useCallback(
    (kind: 'board' | 'database', id: string, sectionId: string | null) => {
      if (place.isPending) return;
      place.mutate({ kind, id, section_id: sectionId }, { onError: fail });
    },
    [place, fail],
  );
  const onDropSection = useCallback(
    (
      id: string,
      parentId: string | null,
      anchor?: { after_id: string } | { before_id: string },
    ) => {
      if (moveSection.isPending) return;
      moveSection.mutate({ id, parent_id: parentId, ...anchor }, { onError: fail });
    },
    [moveSection, fail],
  );

  // a resource whose section this viewer's list does not know is shown at the root
  const known = useMemo(() => new Set(sectionItems.map((s) => s.id)), [sectionItems]);
  const containerOf = (id: string | null | undefined) => (id && known.has(id) ? id : null);

  const boardTile = (
    board: Board,
    siblings: readonly Board[],
    index: number,
    container: string | null,
    location?: string,
  ) => {
    const mayEdit = board.can_edit_details === true;
    const order =
      !narrowed && mayEdit
        ? {
            previous: siblings[index - 1],
            next: siblings[index + 1],
            pending: place.isPending || moveBoard.isPending,
            // inside its own container it is the plain reorder; from another one it is a filing
            onMove: (anchor: { after_id: string } | { before_id: string }) => {
              moveBoard.mutate({ id: board.id, ...anchor }, { onError: fail });
            },
            onDropBeside: (
              sourceId: string,
              anchor: { after_id: string } | { before_id: string },
            ) => {
              const source = boardList.find((b) => b.id === sourceId);
              if (source && containerOf(source.section_id) === container) {
                moveBoard.mutate({ id: sourceId, ...anchor }, { onError: fail });
              } else {
                place.mutate(
                  { kind: 'board', id: sourceId, section_id: container, ...anchor },
                  { onError: fail },
                );
              }
            },
          }
        : undefined;
    return (
      <BoardTile
        key={board.id}
        board={board}
        order={order}
        location={location}
        onMoveToSection={
          mayEdit && filing
            ? () => {
                setMoving({
                  kind: 'board',
                  id: board.id,
                  name: board.name,
                  sectionId: board.section_id ?? null,
                });
              }
            : undefined
        }
      />
    );
  };
  const databaseCard = (
    database: Database,
    siblings: readonly Database[],
    index: number,
    container: string | null,
    location?: string,
  ) => (
    <DatabaseCard
      key={database.id}
      database={database}
      orderable={!narrowed}
      previous={siblings[index - 1]}
      next={siblings[index + 1]}
      location={location}
      onDropBeside={
        !narrowed
          ? (sourceId, anchor) => {
              const source = databaseList.find((d) => d.id === sourceId);
              if (source && containerOf(source.section_id) === container) {
                moveDatabase.mutate({ id: sourceId, ...anchor }, { onError: fail });
              } else {
                place.mutate(
                  { kind: 'database', id: sourceId, section_id: container, ...anchor },
                  { onError: fail },
                );
              }
            }
          : undefined
      }
      onMoveToSection={
        dbCan(database, 'database.edit_details') && filing
          ? () => {
              setMoving({
                kind: 'database',
                id: database.id,
                name: database.name,
                sectionId: database.section_id ?? null,
              });
            }
          : undefined
      }
    />
  );

  const flat = useMemo(() => {
    const opened = new Map((userId ? readRecentBoardIds(userId) : []).map((id, i) => [id, i]));
    const openedDatabases = new Map(
      (userId ? readRecentDatabaseIds(userId) : []).map((id, i) => [id, i]),
    );
    const where = (id: string | null | undefined) => (id ? pathLabel(sectionItems, id) : undefined);
    const entries: Entry[] = [
      ...boardList.map((board, index): Entry => ({
        key: `board:${board.id}`,
        kind: 'board',
        name: board.name,
        manual: index,
        rank: opened.get(board.id) ?? Number.MAX_SAFE_INTEGER,
        filterable: {
          kind: 'board',
          favorite: board.is_favorite,
          recent: opened.has(board.id),
          ownerId: board.owner_user_id,
          space: null,
          haystack: [board.name.toLocaleLowerCase()],
        },
        node: boardTile(board, [], index, null, where(board.section_id)),
      })),
      ...databaseList.map((database, index): Entry => ({
        key: `database:${database.id}`,
        kind: 'database',
        name: database.name,
        manual: boardList.length + index,
        rank: openedDatabases.get(database.id) ?? Number.MAX_SAFE_INTEGER,
        filterable: {
          kind: 'database',
          favorite: database.is_favorite,
          recent: openedDatabases.has(database.id),
          ownerId: database.owner_user_id,
          space: null,
          haystack: [database.name.toLocaleLowerCase()],
        },
        node: databaseCard(database, [], index, null, where(database.section_id)),
      })),
    ];
    return entries
      .filter((entry) => matchesFilters(entry.filterable, filters, userId, query, offered))
      .sort((a, b) =>
        sort === 'name'
          ? a.name.localeCompare(b.name, locale) || a.manual - b.manual
          : sort === 'opened'
            ? a.rank - b.rank || a.manual - b.manual
            : a.manual - b.manual,
      );
    // the card builders only close over stable callbacks and the section list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    boardList,
    databaseList,
    filters,
    query,
    sort,
    locale,
    userId,
    sectionItems,
    filing,
    place.isPending,
  ]);

  const resourcesOf = (container: string | null): ReactNode[] => {
    const bs = boardList.filter((b) => containerOf(b.section_id) === container);
    const dbs = databaseList.filter((d) => containerOf(d.section_id) === container);
    return [
      ...bs.map((board, index) => boardTile(board, bs, index, container)),
      ...dbs.map((database, index) => databaseCard(database, dbs, index, container)),
    ];
  };

  const wanted = [
    ...(kinds.includes('board') ? [boards] : []),
    ...(kinds.includes('database') ? [databases] : []),
  ];
  const failed = wanted.find((source) => source.isError);
  if (wanted.some((source) => source.isPending)) return <LoadingState />;
  if (failed) return <ErrorState error={failed.error} onRetry={() => void failed.refetch()} />;
  const nothing = boardList.length + databaseList.length === 0;
  if (nothing && sectionItems.length === 0) {
    return <EmptyState title={t('workspace.objectsEmpty')} />;
  }

  const dialogs = (
    <>
      {naming && (
        <SectionNameDialog
          workspaceId={workspaceId}
          parent={naming.parent}
          section={naming.section}
          onClose={() => {
            setNaming(null);
          }}
        />
      )}
      {deleting && (
        <DeleteSectionDialog
          workspaceId={workspaceId}
          section={deleting}
          looksEmpty={
            !sectionItems.some((s) => s.parent_id === deleting.id) &&
            !boardList.some((b) => b.section_id === deleting.id) &&
            !databaseList.some((d) => d.section_id === deleting.id)
          }
          onClose={() => {
            if (focusId === deleting.id) onFocus(deleting.parent_id);
            setDeleting(null);
          }}
        />
      )}
      {moving && (
        <MoveToSectionDialog
          workspaceId={workspaceId}
          subject={moving}
          sections={sectionItems}
          maxDepth={maxDepth}
          onClose={() => {
            setMoving(null);
          }}
        />
      )}
    </>
  );

  if (narrowed) {
    const needle = query.trim().toLocaleLowerCase();
    const matchingSections =
      needle === '' ? [] : sectionItems.filter((s) => s.name.toLocaleLowerCase().includes(needle));
    if (flat.length === 0 && matchingSections.length === 0) {
      return (
        <p className={styles.noMatches} role="status">
          {activeFilterCount(filters, offered) > 0
            ? t('catalog.noMatches')
            : t('workspace.objectsNoMatches')}
        </p>
      );
    }
    return (
      <>
        {matchingSections.length > 0 && (
          <section aria-label={t('sections.searchResults')} className={styles.sectionHits}>
            <h3 className={styles.sectionHitsTitle}>{t('sections.searchResults')}</h3>
            <ul>
              {matchingSections.map((section) => (
                <li key={section.id}>
                  <button
                    type="button"
                    data-testid="section-hit"
                    onClick={() => {
                      onFocus(section.id);
                    }}
                  >
                    <Icons.folder /> <strong>{section.name}</strong>
                    <span className={styles.hitPath}>
                      {pathLabel(sectionItems, section.parent_id)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        {flat.length > 0 && (
          <ul className={styles.objectGrid} data-testid="workspace-objects">
            {flat.map((item) => item.node)}
          </ul>
        )}
        {dialogs}
      </>
    );
  }

  return (
    <>
      {focus && <Breadcrumbs sections={sectionItems} focus={focus} onFocus={onFocus} />}
      <SectionTree
        roots={roots}
        focus={focus}
        shared={{
          workspaceId,
          all: sectionItems,
          maxDepth,
          canManage,
          collapsed,
          resources: resourcesOf,
          onDropResource,
          onDropSection,
          onFocus,
          onToggle: toggle,
          onCreateSub: (parent) => {
            setNaming({ parent });
          },
          onRename: (section) => {
            setNaming({ parent: null, section });
          },
          onMoveSection: (section) => {
            setMoving({
              kind: 'section',
              id: section.id,
              name: section.name,
              sectionId: section.parent_id,
            });
          },
          onDelete: setDeleting,
        }}
      />
      {dialogs}
    </>
  );
}
